// Phishing Scenarios - Personalization Variables

import { isGenericLogoSlug } from '@/lib/brand-logos/generic-logo-slugs'
import { GENERIC_ORG_LOGO_PATH } from '@/lib/brand-logos/generic-org-logo'
import { tenantPreviewLogo } from '@/lib/brand-logos/tenant-preview-logo'

// Variable category types
export type VariableCategory = 'recipient' | 'company' | 'campaign' | 'datetime'

// Personalization variable definition
export interface PersonalizationVariable {
  id: string
  name: string
  token: string
  description: string
  category: VariableCategory
  example: string
}

// Category display configuration
export const VARIABLE_CATEGORY_CONFIG: Record<VariableCategory, { label: string; icon: string }> = {
  recipient: { label: 'Recipient', icon: 'User' },
  company: { label: 'Company', icon: 'Building2' },
  campaign: { label: 'Campaign', icon: 'Mail' },
  datetime: { label: 'Date & Time', icon: 'Calendar' },
}

// All available personalization variables.
//
// Kept in PARITY with the backend send/render-time substitution map
// (audit 2026-05-15). Only tokens the BE actually substitutes are
// offered here — a token offered but not substituted ships LITERALLY
// to the recipient. Dead tokens removed in the 2026-05-15 audit:
// {{manager}}, {{companyDomain}}, {{office}} (never threaded by BE) and
// {{campaignName}} (BE substitutes {{workspace}} = campaign name on
// landing pages only — not in the email body, so it is not an
// email-authoring picker variable).
export const personalizationVariables: PersonalizationVariable[] = [
  // Recipient variables
  {
    id: 'firstName',
    name: 'First Name',
    token: '{{firstName}}',
    description: "Recipient's first name",
    category: 'recipient',
    example: 'Alex',
  },
  {
    id: 'lastName',
    name: 'Last Name',
    token: '{{lastName}}',
    description: "Recipient's last name",
    category: 'recipient',
    example: 'Smith',
  },
  {
    id: 'fullName',
    name: 'Full Name',
    token: '{{fullName}}',
    description: "Recipient's full name",
    category: 'recipient',
    example: 'Alex Smith',
  },
  {
    id: 'email',
    name: 'Email',
    token: '{{email}}',
    description: "Recipient's email address",
    category: 'recipient',
    example: 'alex.smith@example.com',
  },
  {
    id: 'jobTitle',
    name: 'Job Title',
    token: '{{jobTitle}}',
    description: "Recipient's job title",
    category: 'recipient',
    example: 'Software Engineer',
  },
  {
    id: 'department',
    name: 'Department',
    token: '{{department}}',
    description: "Recipient's department",
    category: 'recipient',
    example: 'Engineering',
  },

  // Company variables
  {
    id: 'company',
    name: 'Company Name',
    token: '{{company}}',
    description: 'Company name',
    category: 'company',
    example: 'Acme Corporation',
  },
  {
    id: 'companyName',
    name: 'Company Name (full)',
    token: '{{companyName}}',
    description: 'Company name — canonical token (alias of {{company}})',
    category: 'company',
    example: 'Acme Corporation',
  },

  // Campaign variables
  {
    // The single BE-substituted token for the phishing-page CTA link, across
    // EVERY attack vector (Link / Data Entry / Attachment / QR / MFA). The BE
    // resolves it to the per-recipient decoy URL — for Data Entry that is the
    // credential-capture page (e.g. /login). There is intentionally no
    // separate `{{phishingUrl}}` token: a token the BE does not substitute
    // ships literally to the recipient (prod incident #478). BUG-12.
    id: 'landingUrl',
    name: 'Phishing Page Link',
    token: '{{landingUrl}}',
    description:
      'Per-recipient link to the phishing page — the credential-capture / Data Entry page. Use this as the CTA link in the email body for any attack vector.',
    category: 'campaign',
    example: 'https://login.acme-portal.example/verify?id=abc123',
  },
  {
    id: 'trainingUrl',
    name: 'Training URL',
    token: '{{trainingUrl}}',
    description:
      "Per-recipient link to the campaign's assigned security training (SCORM player)",
    category: 'campaign',
    example: 'https://app.humanfirewall.me/player/abc123',
  },
  {
    // BUG-08 — `{{qrCode}}` was BE-substituted (campaign-send.processor)
    // since 2026-05-14 but never listed here, so it was invisible in the
    // Insert Variable picker. The BE swaps it for a full <img> QR tag at
    // send time. Use this for QR-vector campaigns.
    id: 'qrCode',
    name: 'QR Code',
    token: '{{qrCode}}',
    description:
      'Drop-in QR code image (full <img> tag) encoding the per-recipient phishing link. For QR-vector campaigns. Use {{qrCodeUrl}} instead if you need the raw image URL inside your own <img> tag.',
    category: 'campaign',
    example: '[QR code image]',
  },
  {
    id: 'qrCodeUrl',
    name: 'QR Code URL',
    token: '{{qrCodeUrl}}',
    description:
      'Raw QR code image URL (PNG data-URI) — place inside your own <img src="{{qrCodeUrl}}"> tag. Use {{qrCode}} for a ready-made image tag.',
    category: 'campaign',
    example: 'data:image/png;base64,…',
  },

  // DateTime variables
  {
    id: 'currentDate',
    name: 'Current Date',
    token: '{{currentDate}}',
    description: "Today's date in local format",
    category: 'datetime',
    example: 'January 17, 2026',
  },
  {
    id: 'currentTime',
    name: 'Current Time',
    token: '{{currentTime}}',
    description: 'Current time',
    category: 'datetime',
    example: '2:30 PM',
  },
  {
    id: 'expirationDate',
    name: 'Expiration Date',
    token: '{{expirationDate}}',
    description: 'Configurable expiration date (e.g., 48 hours from now)',
    category: 'datetime',
    example: 'January 19, 2026',
  },
  {
    id: 'currentYear',
    name: 'Current Year',
    token: '{{currentYear}}',
    description: 'Current 4-digit year — used in footers (e.g. © {{currentYear}} Microsoft)',
    category: 'datetime',
    example: '2026',
  },
]

// Get variables by category
export function getVariablesByCategory(category: VariableCategory): PersonalizationVariable[] {
  return personalizationVariables.filter(v => v.category === category)
}

// Get all categories with their variables
export function getVariablesGroupedByCategory(): Record<VariableCategory, PersonalizationVariable[]> {
  return {
    recipient: getVariablesByCategory('recipient'),
    company: getVariablesByCategory('company'),
    campaign: getVariablesByCategory('campaign'),
    datetime: getVariablesByCategory('datetime'),
  }
}

// Search variables by name or description
export function searchVariables(query: string): PersonalizationVariable[] {
  const lowerQuery = query.toLowerCase()
  return personalizationVariables.filter(v =>
    v.name.toLowerCase().includes(lowerQuery) ||
    v.description.toLowerCase().includes(lowerQuery) ||
    v.token.toLowerCase().includes(lowerQuery)
  )
}

// Get variable by token
export function getVariableByToken(token: string): PersonalizationVariable | undefined {
  return personalizationVariables.find(v => v.token === token)
}

// Sample data for preview
// Logo.dev CDN token — same public key BE uses at send-time (see
// admin-backend-new.humanfirewall.ai/src/database/seed/phishing/brand-logos.ts).
// Safe to expose client-side (public key). `pk_frbecace72bb75a2ff58d3`
// (previously here) was invalid — returned 404 for all domains.
const LOGO_DEV_TOKEN = 'pk_SwxLuoCQQ9K-8prVxyylJQ'

/**
 * BE logo proxy URL base — prefers the same API_BASE the rest of the FE
 * uses so logos are served from `${API_BASE}/v1/logos/<domain>` and share
 * the BE's `public/logos/cached/` filesystem cache (warm once per brand
 * per tenant, served to every subsequent user). Falls back to logo.dev
 * direct only when API_BASE isn't set (should never happen in prod).
 *
 * The client-side service worker at `public/sw-logos.js` intercepts
 * requests to this path and stores responses in Cache Storage for
 * instant offline reads across tab reloads.
 */
const API_BASE =
  typeof process !== 'undefined' && process.env.NEXT_PUBLIC_API_URL
    ? process.env.NEXT_PUBLIC_API_URL.replace(/\/$/, '')
    : ''

// Neutral QR-code placeholder for template previews (BUG-08). The real QR
// is generated per-recipient at send time; preview just needs to show the
// author where the code lands without rendering a literal {{qrCode}}.
const QR_PREVIEW_IMG_SRC =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160">' +
      '<rect width="160" height="160" fill="#f3f4f6" stroke="#9ca3af"/>' +
      '<rect x="16" y="16" width="36" height="36" fill="#111827"/>' +
      '<rect x="108" y="16" width="36" height="36" fill="#111827"/>' +
      '<rect x="16" y="108" width="36" height="36" fill="#111827"/>' +
      '<text x="80" y="86" font-family="sans-serif" font-size="13" fill="#374151" text-anchor="middle">QR Code</text>' +
      '<text x="80" y="102" font-family="sans-serif" font-size="9" fill="#9ca3af" text-anchor="middle">generated at send</text>' +
      '</svg>',
  )

export const samplePreviewData: Record<string, string> = {
  // ONE sample recipient across every preview surface (#578). These recipient
  // tokens are pinned to the backend PREVIEW_SAMPLE_RECIPIENT constant by
  // apps/api/.../__tests__/preview-sample-recipient-parity.spec.ts, so the
  // email body, the subject line and the awareness page all greet the same
  // person (previously the body said "John" while the awareness page said
  // "Alex" in the very same drawer).
  '{{firstName}}': 'Alex',
  '{{lastName}}': 'Smith',
  '{{fullName}}': 'Alex Smith',
  '{{email}}': 'alex.smith@example.com',
  '{{jobTitle}}': 'Software Engineer',
  '{{designation}}': 'Software Engineer',
  '{{department}}': 'Engineering',
  '{{company}}': 'Acme Corporation',
  // {{companyName}} is the BE-canonical alias added 2026-05-14. The wizard
  // preview must mirror the send-time BE substitution map; without this
  // entry the preview rendered `{{companyName}}` as literal text while the
  // delivered email showed it BLANK (Pass-2 strip), confusing template
  // authors — prod ticket #253.
  '{{companyName}}': 'Acme Corporation',
  // {{workspace}} = campaign name; BE substitutes it on landing pages.
  '{{workspace}}': 'Acme Corporation',
  // {{campaignName}} is NOT a BE email-body token (removed from the
  // picker in the 2026-05-15 parity audit). Kept here only so a legacy
  // landing-page template still authored with it renders cleanly in
  // preview instead of leaking the literal token.
  '{{campaignName}}': 'Q1 Security Awareness',
  '{{landingUrl}}': 'https://track.example.com/preview',
  // {{trackedLink}} / {{trackingLink}} / {{tracked_link}} aliases — all
  // resolve to the same decoy URL at send time via
  // `EmailBuilderService.replaceLandingUrlPlaceholder`. Preview substitutes
  // them to the same neutral preview URL so authors get an accurate render.
  '{{trackedLink}}': 'https://track.example.com/preview',
  '{{trackingLink}}': 'https://track.example.com/preview',
  '{{tracked_link}}': 'https://track.example.com/preview',
  '{{trackingUrl}}': 'https://track.example.com/preview',
  // QR-code tokens (BUG-08). At send time the BE swaps {{qrCode}} for a
  // full <img> QR tag and {{qrCodeUrl}} for the raw PNG data-URI. Preview
  // renders a neutral placeholder so the author sees the QR's placement
  // without leaking the literal token.
  '{{qrCode}}': `<img src="${QR_PREVIEW_IMG_SRC}" alt="QR code" width="160" height="160" style="display:block;margin:0 auto;" />`,
  '{{qrCodeUrl}}': QR_PREVIEW_IMG_SRC,
  // Logo URL — a lure writes `<img src="{{logoUrl}}" alt="DHL">` for the brand
  // it imitates, so with nothing to say which brand this preview is of, it
  // shows the neutral logo: not the customer's, whose mark must never stand on
  // an imitated brand's mail, and not HumanFirewall's, which would name the
  // platform as the fake. Callers with scenario context pass `brandDomain` to
  // `replaceVariablesWithSampleData` for the accurate brand logo.
  '{{logoUrl}}': GENERIC_ORG_LOGO_PATH,
  '{{securityCode}}': '123456',
  '{{date}}': new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }),
  '{{currentDate}}': new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }),
  '{{currentTime}}': new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }),
  '{{expirationDate}}': new Date(Date.now() + 48 * 60 * 60 * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }),
  '{{currentYear}}': String(new Date().getFullYear()),
  // {{callbackNumber}} — Reply-vector callback phone. Resolved at send time
  // from the email template's replyConfig; preview substitutes a neutral
  // sample so the author can verify placement/formatting (BUG-23).
  '{{callbackNumber}}': '+1 415 555 0142',
  // Awareness-page server-side tokens. At send time the BE swaps these
  // for the live campaign target's training link, IDs, and API origin.
  // Preview substitution uses neutral placeholders so the rendered
  // awareness page reads cleanly instead of leaking `{{trainingUrl}}`.
  '{{trainingUrl}}': '/training/preview',
  '{{apiEndpoint}}': API_BASE || 'https://api.humanfirewall.example',
  '{{targetId}}': 'preview-target-id',
  '{{landingPageId}}': 'preview-landing-id',
  '{{pageTitle}}': 'Security Awareness',
}

/**
 * Derive a brand domain (e.g. "microsoft.com") from a scenario's decoy
 * landingBaseUrl (e.g. "microsoft.youmint.net"). Strips the youmint
 * suffix and swaps in .com. Used so the preview renders an accurate
 * brand logo for that scenario.
 */
export function brandDomainFromLandingUrl(
  landingBaseUrl: string | null | undefined,
): string {
  if (!landingBaseUrl) return 'humanfirewall.com'
  return landingBaseUrl.replace(/\.youmint\.(net|org)$/, '.com')
}

/**
 * Build a brand-scoped logoUrl. Routes through the @Public BE preview
 * endpoint `/v1/brand-logos/resolve-preview?slug=<slug>` which 302s to
 * the resolved PNG. Works inside <img> tags (no Bearer token required)
 * and participates in the service-worker cache. Falls back to logo.dev
 * only when the API base isn't resolvable (SSR build, env misconfig).
 *
 * Size is unused — the BE serves a canonical PNG; scaling happens via
 * CSS. Kept in the signature for call-site compatibility.
 *
 * A domain that names no brand (a scenario with no decoy reads
 * `humanfirewall.com`) is a generic lure: the send draws the employer's logo,
 * else the neutral one, and the preview draws the same. Scoped to the generic
 * slugs on purpose: a named brand never takes the customer's logo.
 */
export function brandLogoUrl(brandDomain: string, _size = 64): string {
  // Convert a full brand domain (e.g. "amazon.com") to the slug the BE
  // uses (e.g. "amazon"). Matches the scenario-selection rendering path
  // which also passes bare slugs and loads correctly on prod.
  const slug = brandDomain.replace(/\.(com|net|org|io|co|ai|me)$/i, "").toLowerCase()
  if (isGenericLogoSlug(slug)) return tenantPreviewLogo() ?? GENERIC_ORG_LOGO_PATH
  if (API_BASE) {
    return `${API_BASE}/v1/brand-logos/resolve-preview?slug=${encodeURIComponent(slug)}`
  }
  return `https://img.logo.dev/${brandDomain}?token=${LOGO_DEV_TOKEN}&size=${_size}`
}

/**
 * Replace all `{{token}}` variables in text with sample data.
 * When `brandDomain` is passed, logoUrl is derived from it (so the preview
 * renders the correct brand logo — otherwise HumanFirewall default).
 */
export function replaceVariablesWithSampleData(
  text: string,
  opts?: { brandDomain?: string },
): string {
  const overrides: Record<string, string> = {}
  if (opts?.brandDomain) {
    overrides['{{logoUrl}}'] = brandLogoUrl(opts.brandDomain)
  }
  const effective = { ...samplePreviewData, ...overrides }

  let result = text
  for (const [token, value] of Object.entries(effective)) {
    result = result.replace(new RegExp(escapeRegExp(token), 'g'), value)
  }
  return result
}

// Helper to escape regex special characters
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// Extract all variable tokens from text
export function extractVariableTokens(text: string): string[] {
  const regex = /\{\{(\w+)\}\}/g
  const matches: string[] = []
  let match
  while ((match = regex.exec(text)) !== null) {
    matches.push(match[0])
  }
  return [...new Set(matches)]
}

// Validate that all variables in text are valid.
// A token is valid if it is offered in the Insert-Variable picker OR is a
// renderer-resolved alias. `samplePreviewData` enumerates every token the
// preview + send paths actually substitute (e.g. {{trackedLink}},
// {{logoUrl}}, {{qrCode}}) — a token in NEITHER set is never substituted
// and ships LITERALLY to the recipient (BUG-245).
export function validateVariables(text: string): { valid: boolean; invalidTokens: string[] } {
  const tokens = extractVariableTokens(text)
  const validTokens = new Set<string>([
    ...personalizationVariables.map(v => v.token),
    ...Object.keys(samplePreviewData),
  ])
  const invalidTokens = tokens.filter(t => !validTokens.has(t))
  return {
    valid: invalidTokens.length === 0,
    invalidTokens,
  }
}
